import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AppToaster } from "@/components/AppToaster";
import { ScanFeedbackHost } from "@/lib/scanFeedback";
import { StartAtTop } from "@/components/StartAtTop";
import { VisibleScreen } from "@/components/VisibleScreen";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ServerUnreachableScreen } from "@/components/ServerStatus";
import { FloorReturnProvider, useFloorReturn } from "@/lib/floorReturn";
import { FloorShell } from "@/floor/FloorShell";
import FloorHome from "@/floor/FloorHome";
import FloorPartCard from "@/floor/FloorPartCard";
import FloorTruckCard from "@/floor/FloorTruckCard";
import FloorLocationCard from "@/floor/FloorLocationCard";
import FloorRapidPhoto from "@/floor/FloorRapidPhoto";
import FloorTagParts from "@/floor/FloorTagParts";
import FloorPutaway from "@/floor/FloorPutaway";
import FloorProblemCard from "@/floor/FloorProblemCard";
import FloorProblems from "@/floor/FloorProblems";
import FloorShelfCleanup from "@/floor/FloorShelfCleanup";
import Login from "@/pages/Login";
import LocationsPage from "@/pages/LocationsPage";
import UsersPage from "@/pages/UsersPage";
import UserDetailPage from "@/pages/UserDetailPage";
import PermissionsPage from "@/pages/PermissionsPage";
import RoleHistoryPage from "@/pages/RoleHistoryPage";
import AuditPage from "@/pages/AuditPage";
import PutawayLogPage from "@/pages/PutawayLogPage";
import LoginHistoryPage from "@/pages/LoginHistoryPage";
import ScanLocationPage from "@/pages/ScanLocationPage";
import ScanPartPage from "@/pages/ScanPartPage";
import ScanDonorPage from "@/pages/ScanDonorPage";
import QuickScanPage from "@/pages/QuickScanPage";
import LabelPage from "@/pages/LabelPage";
import LocationLabelsPage from "@/pages/LocationLabelsPage";
import DonorsPage from "@/pages/DonorsPage";
import DonorDetailPage from "@/pages/DonorDetailPage";
import DonorEditPage from "@/pages/DonorEditPage";
import PartsPage from "@/pages/PartsPage";
import RequestsPage from "@/pages/RequestsPage";
import PartDetailPage from "@/pages/PartDetailPage";
import ReturnsPage from "@/pages/ReturnsPage";
import TransfersPage from "@/pages/TransfersPage";
import PutawayPage from "@/pages/PutawayPage";
import SearchPage from "@/pages/SearchPage";
import BulkFixPage from "@/pages/BulkFixPage";
import CatchUpToolsPage from "@/pages/CatchUpToolsPage";
import SearchRecordPage from "@/pages/SearchRecordPage";
import SearchSynonymsPage from "@/pages/SearchSynonymsPage";
import BulkUploadPage from "@/pages/BulkUploadPage";
import WaitingDataplatesPage from "@/pages/WaitingDataplatesPage";
import DataPage from "@/pages/DataPage";
import BackupsPage from "@/pages/BackupsPage";
import PhotoMaintenancePage from "@/pages/PhotoMaintenancePage";
import ContainerListsPage from "@/pages/ContainerListsPage";
import SecurityPage from "@/pages/SecurityPage";
import HomePage from "@/pages/HomePage";
import DismantlePhotosPage from "@/pages/DismantlePhotosPage";
import TagToPhotosPage from "@/pages/TagToPhotosPage";
import RapidCapturePage from "@/pages/RapidCapturePage";
import YardProductionPage from "@/pages/YardProductionPage";
import MakeTagsPage from "@/pages/MakeTagsPage";
import PullCartsPage from "@/pages/PullCartsPage";
import PickPage from "@/pages/PickPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import FieldCapturePage from "@/pages/FieldCapturePage";
import MoveTotePage from "@/pages/MoveTotePage";
import MoveItemPage from "@/pages/MoveItemPage";
import MovesHistoryPage from "@/pages/MovesHistoryPage";
import MovementHistoryPage from "@/pages/MovementHistoryPage";
import RecycleBatchesPage from "@/pages/RecycleBatchesPage";
import WorkBinPage from "@/pages/WorkBinPage";
import SortQueuePage from "@/pages/SortQueuePage";
import SortIdentifyDetailPage from "@/pages/SortIdentifyDetailPage";
import AskAiGuidePage from "@/pages/AskAiGuidePage";
import ReviewQueuePage from "@/pages/ReviewQueuePage";
import AiSettingsPage from "@/pages/AiSettingsPage";
import TaxonomyAuditPage from "@/pages/TaxonomyAuditPage";
import VehicleSetupPage from "@/pages/VehicleSetupPage";
import FloorButtonsPage from "@/pages/FloorButtonsPage";
import PrinterSettingsPage from "@/pages/PrinterSettingsPage";
import ManagedListsPage from "@/pages/ManagedListsPage";
import NamingPage from "@/pages/NamingPage";
import DailyDigestPage from "@/pages/DailyDigestPage";
import ProtectedChangesPage from "@/pages/ProtectedChangesPage";
import HealthChecksPage from "@/pages/HealthChecksPage";
import SalesPage from "@/pages/SalesPage";

function Shell({ children, role, permission }) {
  const { user, has } = useAuth();
  const fr = useFloorReturn();
  // Floor-locked staff (no Desk surface) may only reach desk routes via an ACTIVE Floor
  // round-trip (tapping a Floor tile). A direct desk URL bounces them back to Floor. This is
  // defense-in-depth on top of the server 403 on /me/preferred-surface + the per-endpoint gates.
  if (user && user.role && !has("use_desk_surface") && !(fr && fr.active)) {
    return <Navigate to="/floor" replace />;
  }
  return (
    <ProtectedRoute role={role} permission={permission}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

// Contains a per-record render crash to a readable error card (surfaces the failing
// field) instead of white-screening the whole page. Keyed by route param so navigating
// to another record resets the boundary.
function Guarded({ label, children }) {
  const params = useParams();
  const key = Object.values(params).join("/") || "root";
  return <RouteErrorBoundary key={key} label={label}>{children}</RouteErrorBoundary>;
}

// Landing router — sends the user to their saved surface (Floor for warehouse techs who
// chose it, Desk otherwise). preferred_surface is server-side per user.
function RootRedirect() {
  const { user, unreachable, sessionUnverified } = useAuth();
  if (user === null) {
    if (unreachable) return <ServerUnreachableScreen />;              // unknown ≠ logged out
    if (sessionUnverified) return <ServerUnreachableScreen variant="unverified" />;   // R-10
    return null;
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={user.preferred_surface === "floor" ? "/floor" : "/home"} replace />;
}

// Floor (tablet/warehouse) route wrapper — no desk sidebar, scan-first shell.
function FloorRoute({ children }) {
  return (
    <ProtectedRoute permission="view_parts">
      <FloorShell>{children}</FloorShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <FloorReturnProvider>
        <AppToaster />
        <ScanFeedbackHost />
        <StartAtTop />
        <VisibleScreen />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="/floor" element={<FloorRoute><FloorHome /></FloorRoute>} />
          <Route path="/floor/rapid-photo" element={<FloorRoute><FloorRapidPhoto /></FloorRoute>} />
          <Route path="/floor/tag" element={<FloorRoute><FloorTagParts /></FloorRoute>} />
          <Route path="/floor/putaway" element={<FloorRoute><FloorPutaway /></FloorRoute>} />
          <Route path="/floor/problems" element={<FloorRoute><FloorProblems /></FloorRoute>} />
          <Route path="/floor/shelve" element={<FloorRoute><FloorShelfCleanup /></FloorRoute>} />
          <Route path="/floor/problem/:id" element={<FloorRoute><FloorProblemCard /></FloorRoute>} />
          <Route path="/floor/part/:id" element={<FloorRoute><Guarded label="this part"><FloorPartCard /></Guarded></FloorRoute>} />
          <Route path="/floor/donor/:id" element={<FloorRoute><Guarded label="this truck"><FloorTruckCard /></Guarded></FloorRoute>} />
          <Route path="/floor/location/:id" element={<FloorRoute><FloorLocationCard /></FloorRoute>} />
          <Route path="/home" element={<Shell><HomePage /></Shell>} />
          <Route path="/donors" element={<Shell permission="search_inventory"><DonorsPage /></Shell>} />
          <Route path="/donors/:id" element={<Shell permission="view_parts"><DonorDetailPage /></Shell>} />
          <Route path="/donors/:id/edit" element={<Shell permission="manage_donors"><DonorEditPage /></Shell>} />
          <Route path="/photo-upload" element={<Shell permission="manage_parts"><DismantlePhotosPage /></Shell>} />
          <Route path="/tag-to-photos" element={<Shell permission="manage_parts"><TagToPhotosPage /></Shell>} />
          <Route path="/rapid-capture" element={<Shell permission="manage_parts"><RapidCapturePage /></Shell>} />
          <Route path="/dismantle" element={<Navigate to="/photo-upload" replace />} />
          <Route path="/capture" element={<Shell permission="manage_parts"><FieldCapturePage /></Shell>} />
          <Route path="/sort-queue" element={<Shell permission="manage_parts"><SortQueuePage /></Shell>} />
          <Route path="/sort-queue/:partId" element={<Shell permission="manage_parts"><Guarded label="this part"><SortIdentifyDetailPage /></Guarded></Shell>} />
          <Route path="/review-queue" element={<Shell permission="review_parts"><ReviewQueuePage /></Shell>} />
          {/* the one-page crew guide — no permission: anyone who can open the app can read how it works */}
          <Route path="/ask-ai-guide" element={<Shell><AskAiGuidePage /></Shell>} />
          <Route path="/ai-settings" element={<Shell permission="manage_ai_budget"><AiSettingsPage /></Shell>} />
          <Route path="/daily-digest" element={<Shell permission="manage_ai_budget"><DailyDigestPage /></Shell>} />
          <Route path="/move-tote" element={<Shell permission="putaway"><MoveTotePage /></Shell>} />
          <Route path="/move-item" element={<Shell permission="putaway"><MoveItemPage /></Shell>} />
          <Route path="/moves-history" element={<Shell permission="putaway"><MovesHistoryPage /></Shell>} />
          <Route path="/parts" element={<Shell permission="search_inventory"><PartsPage /></Shell>} />
          <Route path="/bulk-fix" element={<Shell permission="bulk_edit_parts"><BulkFixPage /></Shell>} />
          <Route path="/catch-up" element={<Shell permission="use_catch_up_tools"><CatchUpToolsPage /></Shell>} />
          <Route path="/search-record" element={<Shell permission="view_search_record"><SearchRecordPage /></Shell>} />
          <Route path="/search-synonyms" element={<Shell permission="approve_search_synonyms"><SearchSynonymsPage /></Shell>} />
          <Route path="/bulk-upload" element={<Shell permission="manage_donors"><BulkUploadPage /></Shell>} />
          <Route path="/dataplates/waiting" element={<Shell permission="manage_donors"><WaitingDataplatesPage /></Shell>} />
          <Route path="/requests" element={<Shell permission="submit_request"><RequestsPage /></Shell>} />
          <Route path="/parts/:id" element={<Shell permission="view_parts"><Guarded label="this part"><PartDetailPage /></Guarded></Shell>} />
          <Route path="/returns" element={<Shell permission="process_return"><ReturnsPage /></Shell>} />
          <Route path="/transfers" element={<Shell permission="view_parts"><TransfersPage /></Shell>} />
          <Route path="/movement-history" element={<Shell permission="search_inventory"><MovementHistoryPage /></Shell>} />
          <Route path="/recycle-batches" element={<Shell permission="view_parts"><RecycleBatchesPage /></Shell>} />
          <Route path="/putaway" element={<Shell permission="putaway"><PutawayPage /></Shell>} />
          <Route path="/work-bin" element={<Shell permission="putaway"><WorkBinPage /></Shell>} />
          <Route path="/search" element={<Shell permission="search_inventory"><SearchPage /></Shell>} />
          <Route path="/quick-scan" element={<Shell permission="view_parts"><QuickScanPage /></Shell>} />
          <Route path="/yard-production" element={<Shell permission="view_operations_metrics"><YardProductionPage /></Shell>} />
          <Route path="/sales" element={<Shell><SalesPage /></Shell>} />
          <Route path="/make-tags" element={<Shell permission="generate_print_labels"><MakeTagsPage /></Shell>} />
          <Route path="/pull-carts" element={<Shell permission="build_cart"><PullCartsPage /></Shell>} />
          <Route path="/pick" element={<Shell permission="pick_remove"><PickPage /></Shell>} />
          <Route path="/pick/:cartId" element={<Shell permission="pick_remove"><PickPage /></Shell>} />
          <Route path="/notifications" element={<Shell permission="manage_settings"><NotificationSettingsPage /></Shell>} />
          <Route path="/data" element={<Shell permission="export_data"><DataPage /></Shell>} />
          <Route path="/backups" element={<Shell permission="manage_backups"><BackupsPage /></Shell>} />
          <Route path="/photo-maintenance" element={<Shell permission="archive_photos"><PhotoMaintenancePage /></Shell>} />
          <Route path="/container-lists" element={<Shell permission="manage_container_lists"><ContainerListsPage /></Shell>} />
          <Route path="/security" element={<Shell><SecurityPage /></Shell>} />
          <Route path="/locations" element={<Shell permission="view_locations"><LocationsPage initialView="fixed" /></Shell>} />
          <Route path="/places" element={<Shell permission="view_locations"><LocationsPage initialView="fixed" /></Shell>} />
          <Route path="/fixed-locations" element={<Shell permission="view_locations"><LocationsPage initialView="fixed" /></Shell>} />
          <Route path="/containers" element={<Shell permission="view_locations"><LocationsPage /></Shell>} />
          <Route path="/label/:id" element={<Shell permission="view_locations"><LabelPage /></Shell>} />
          <Route path="/shelf-labels" element={<Shell permission="generate_print_labels"><LocationLabelsPage /></Shell>} />
          <Route path="/scan/loc/:token" element={<Shell permission="view_locations"><ScanLocationPage /></Shell>} />
          <Route path="/scan/part/:token" element={<Shell permission="view_parts"><ScanPartPage /></Shell>} />
          <Route path="/scan/donor/:token" element={<Shell permission="view_parts"><ScanDonorPage /></Shell>} />
          <Route path="/users" element={<Shell permission="manage_users"><UsersPage /></Shell>} />
          <Route path="/users/:id" element={<Shell permission="manage_users"><UserDetailPage /></Shell>} />
          <Route path="/permissions" element={<Shell permission="manage_roles_permissions"><PermissionsPage /></Shell>} />
          <Route path="/permissions/history" element={<Shell permission="manage_roles_permissions"><RoleHistoryPage /></Shell>} />
          <Route path="/taxonomy-audit" element={<Shell permission="manage_taxonomy"><TaxonomyAuditPage /></Shell>} />
          <Route path="/vehicle-setup" element={<Shell permission="manage_lists"><VehicleSetupPage /></Shell>} />
          <Route path="/floor-buttons" element={<Shell permission="manage_lists"><FloorButtonsPage /></Shell>} />
          <Route path="/printer-settings" element={<Shell permission="generate_print_labels"><PrinterSettingsPage /></Shell>} />
          <Route path="/find-truck" element={<Navigate to="/donors" replace />} />
          <Route path="/managed-lists" element={<Shell permission="manage_lists"><ManagedListsPage /></Shell>} />
          <Route path="/naming" element={<Shell permission="manage_lists"><NamingPage /></Shell>} />
          <Route path="/audit" element={<Shell permission="view_audit_log"><AuditPage /></Shell>} />
          <Route path="/protected-changes" element={<Shell permission="view_protected_changes"><ProtectedChangesPage /></Shell>} />
          {/* the checks themselves are permission-filtered server-side, so the route only needs the desk */}
          <Route path="/health" element={<Shell permission="view_system_health"><HealthChecksPage /></Shell>} />
          <Route path="/putaway-log" element={<Shell permission="view_audit_log"><PutawayLogPage /></Shell>} />
          <Route path="/login-history" element={<Shell permission="view_login_history"><LoginHistoryPage /></Shell>} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        </FloorReturnProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
